import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, ButtonBase, IconButton, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import UnlayerEditor from '../../../components/UnlayerEditor';
import {
    Editor as SimpleWysiwygEditor, EditorProvider, Toolbar,
    BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough,
    BtnNumberedList, BtnBulletList, BtnLink, BtnClearFormatting,
    BtnUndo, BtnRedo, Separator,
} from 'react-simple-wysiwyg';
import { Section, PdiRow as Row } from '../style/cards/_PdiHelpers';
import { useWebshopLanguages } from '../WebshopLanguagesContext';

const ALL_LANGS = [
    { code: 'nl', short: 'NL', label: 'Dutch'   },
    { code: 'en', short: 'GB', label: 'English' },
    { code: 'fr', short: 'FR', label: 'French'  },
];

const DEFAULT_STATIC_PAGE = {
    iframe: false,
    editor: 'wysiwyg',                                     // 'wysiwyg' | 'unlayer'
    html:    { nl: '', en: '', fr: '' },
    designs: { nl: null, en: null, fr: null },
};

/**
 * Reusable single-page editor for About / Disclaimer / Privacy. They all
 * share the same shape in the legacy admin: one fixed page (no list), an
 * iframe toggle, three language tabs with a layout-copy shortcut, and a
 * content area that can be either a WYSIWYG (default for short legal text)
 * or the unlayer designer (for richer About-style pages).
 */
const StaticPageTab = ({
    theme,
    t,
    pageKey,
    defaultTitle,
    defaultDescription,
    value,
    onChange,
    hideHeader = false,
}) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    // Controlled when the parent passes value + onChange (so its save flow
    // can persist the content); otherwise self-contained local state so the
    // component still works in isolation. Updaters are forwarded as functions
    // so the parent can apply them against its latest state (avoids stale
    // closures in the debounced unlayer auto-sync below).
    const controlled = value !== undefined && typeof onChange === 'function';
    const [localConfig, setLocalConfig] = useState(DEFAULT_STATIC_PAGE);
    const config = controlled ? (value || DEFAULT_STATIC_PAGE) : localConfig;
    const setConfig = controlled ? onChange : setLocalConfig;
    const [langTab, setLangTab] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);
    // Only show language tabs the admin is allowed to edit (ADMIN_LANGUAGES).
    const { adminLanguages } = useWebshopLanguages();
    const LANGS = ALL_LANGS.filter(l => adminLanguages.includes(String(l.code).toUpperCase()));
    const activeLangIdx = (langTab >= 0 && langTab < LANGS.length) ? langTab : 0;
    const activeLang = (LANGS[activeLangIdx] || ALL_LANGS[0]).code;

    // Live ref to the active language so the (once-bound) unlayer
    // design:updated listener always writes to the language on screen.
    const activeLangRef = useRef(activeLang);
    useEffect(() => { activeLangRef.current = activeLang; }, [activeLang]);

    // ESC exits fullscreen — common reflex.
    useEffect(() => {
        if (!fullscreen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const setHtml = (lang, val) => setConfig(p => ({ ...p, html: { ...(p.html || {}), [lang]: val } }));

    // F24: buffer wysiwyg keystrokes locally and flush upstream on a short
    // debounce. Fully controlled, every keystroke re-rendered the whole
    // config screen AND re-ran the staticPages dirty check — a double
    // JSON.stringify over 6 pages x 3 languages incl. the unlayer design
    // blobs. Same pattern as AboutPageTab's ClassicDayRow. The flush also
    // fires on blur / language switch / editor-mode switch / unmount so the
    // parent state (and the global Save) never misses the tail keystrokes.
    const [draftHtml, setDraftHtml] = useState(null); // null = mirror parent state
    const draftRef = useRef(null);   // { lang, val } pending flush
    const draftTimerRef = useRef(null);
    const flushDraft = () => {
        if (draftTimerRef.current) { clearTimeout(draftTimerRef.current); draftTimerRef.current = null; }
        const d = draftRef.current;
        if (d) { draftRef.current = null; setDraftHtml(null); setHtml(d.lang, d.val); }
    };
    const flushDraftRef = useRef(flushDraft);
    flushDraftRef.current = flushDraft;
    useEffect(() => () => { flushDraftRef.current(); }, []);
    const onWysiwygChange = (val) => {
        setDraftHtml(val);
        draftRef.current = { lang: activeLang, val };
        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        draftTimerRef.current = setTimeout(() => flushDraftRef.current(), 300);
    };

    const copyLayoutFrom = (fromLang) => {
        flushDraft();
        if (fromLang === activeLang) return;
        if (config.editor === 'wysiwyg') {
            setHtml(activeLang, config.html?.[fromLang] || '');
        } else {
            const src = config.designs?.[fromLang];
            if (!src) return;
            setConfig(p => ({ ...p, designs: { ...(p.designs || {}), [activeLang]: src } }));
            if (editorRef.current?.editor) {
                try { editorRef.current.editor.loadDesign(src); } catch { /* ignore */ }
            }
        }
    };

    const switchEditor = (mode) => { flushDraft(); setConfig(p => ({ ...p, editor: mode })); };

    // ── unlayer wiring ─────────────────────────────────────────────
    const editorRef = useRef(null);
    // F28: guard against stacked design:updated listeners — onReady fires
    // again on editor re-init (wysiwyg<->unlayer toggle, remount) and each
    // registration added another listener + debounce timer. Also lets unmount
    // cancel a pending sync so it can't setState afterwards.
    const syncBoundRef = useRef(null);
    const syncTimerRef = useRef(null);
    useEffect(() => () => {
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
        const bound = syncBoundRef.current;
        if (bound) {
            try { bound.ed.removeEventListener && bound.ed.removeEventListener('design:updated', bound.fn); } catch { /* ignore */ }
        }
    }, []);
    const prevLangRef = useRef(activeLang);

    // Persist the current language's design before switching, then load the
    // new language's design into the editor. Same approach as DynamicPageTab.
    useEffect(() => {
        if (config.editor !== 'unlayer') return;
        const ed = editorRef.current?.editor;
        if (!ed) return;
        const prevLang = prevLangRef.current;

        const loadCurrent = () => {
            const design = config.designs?.[activeLang];
            try {
                if (design) ed.loadDesign(design);
                else        ed.loadBlank?.();
            } catch { /* ignore */ }
        };

        if (prevLang !== activeLang) {
            try {
                ed.saveDesign((design) => {
                    setConfig(p => ({ ...p, designs: { ...(p.designs || {}), [prevLang]: design } }));
                    loadCurrent();
                });
            } catch { loadCurrent(); }
        }
        prevLangRef.current = activeLang;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLang, config.editor]);

    return (
        <Box>
            {/* Header — hidden when embedded (e.g. inside the About page, which
                has its own header + store navigator). */}
            {!hideHeader && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {tr(`cfg_static_${pageKey}_title`, defaultTitle)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tr(`cfg_static_${pageKey}_desc`, defaultDescription)}
                    </Typography>
                </Box>
            )}

            <Stack spacing={2}>
                {/* Settings */}
                <Section
                    anchor="cfg-card-static-settings"
                    title={tr('cfg_static_settings_title', 'Page settings')}
                    description={tr('cfg_static_settings_desc', 'How the page is rendered on the storefront.')}
                >
                    <Row
                        label={tr('cfg_static_iframe', 'Render the page in an iframe')}
                        help={tr('cfg_static_iframe_help', 'Recommended when the content uses advanced styling or scripts that should not interfere with the rest of the shop.')}
                        control={
                            <Switch
                                color="success" size="small"
                                checked={!!config.iframe}
                                onChange={(e) => setConfig(p => ({ ...p, iframe: e.target.checked }))}
                            />
                        }
                    />
                </Section>

                {/* Content */}
                <Section
                    anchor="cfg-card-static-content"
                    title={tr('cfg_static_content_title', 'Page content')}
                    description={tr('cfg_static_content_desc', 'The body shown on the page. Each language has its own version — copy from another language with the buttons on the right.')}
                    divider={false}
                >
                    {/* Editor frame — wraps the control row + the editor body
                        in one box. When fullscreen, the wrapper goes fixed so
                        the entire control row travels with it (lang tabs,
                        copy-from buttons, fullscreen toggle). */}
                    {fullscreen && (
                        /* Placeholder so the surrounding layout doesn't
                           collapse while the editor is detached. */
                        <Box sx={{ minHeight: 660, border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                {tr('cfg_static_fullscreen_active', 'Editor in fullscreen — press ESC or the exit button to return.')}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={fullscreen ? {
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2000,
                        backgroundColor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2,
                        gap: 1.5,
                    } : {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}>
                        {/* Control row — lang tabs left, copy buttons + fullscreen toggle right */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
                            <Tabs
                                value={activeLangIdx}
                                onChange={(_e, v) => { flushDraft(); setLangTab(v); }}
                                sx={{
                                    minHeight: 36,
                                    '& .MuiTab-root': { minHeight: 36, px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.85rem', minWidth: 0 },
                                    '& .MuiTabs-indicator': { height: 2 },
                                }}
                            >
                                {LANGS.map(l => (
                                    <Tab
                                        key={l.code}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', letterSpacing: 0.5 }}>{l.short}</Typography>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{l.label}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {tr('cfg_static_layout_from', 'Copy layout from:')}
                                </Typography>
                                {LANGS.filter((_, i) => i !== activeLangIdx).map(l => (
                                    <Button
                                        key={l.code}
                                        size="small" variant="outlined"
                                        onClick={() => copyLayoutFrom(l.code)}
                                        sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 0, px: 1.25 }}
                                    >
                                        {l.label}
                                    </Button>
                                ))}
                                {/* Fullscreen toggle — only meaningful for the unlayer designer */}
                                {config.editor === 'unlayer' && (
                                    <IconButton
                                        size="small"
                                        onClick={() => setFullscreen(v => !v)}
                                        title={fullscreen ? tr('cfg_static_exit_fullscreen', 'Exit fullscreen') : tr('cfg_static_fullscreen', 'Fullscreen')}
                                        sx={{ ml: 0.5 }}
                                    >
                                        {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                        {/* Editor body — wysiwyg OR unlayer */}
                        {config.editor === 'wysiwyg' ? (
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
                                '& .rsw-editor':  { minHeight: 280, border: 'none' },
                                '& .rsw-toolbar': { borderBottom: '1px solid', borderColor: 'divider' },
                                '& .rsw-ce':      { minHeight: 240, padding: '12px 16px' },
                            }}>
                                <EditorProvider>
                                    <SimpleWysiwygEditor
                                        value={draftHtml ?? (config.html?.[activeLang] || '')}
                                        onChange={(e) => onWysiwygChange(e.target.value)}
                                        onBlur={flushDraft}
                                    >
                                        <Toolbar>
                                            <BtnUndo />
                                            <BtnRedo />
                                            <Separator />
                                            <BtnBold />
                                            <BtnItalic />
                                            <BtnUnderline />
                                            <BtnStrikeThrough />
                                            <Separator />
                                            <BtnNumberedList />
                                            <BtnBulletList />
                                            <Separator />
                                            <BtnLink />
                                            <BtnClearFormatting />
                                        </Toolbar>
                                    </SimpleWysiwygEditor>
                                </EditorProvider>
                            </Box>
                        ) : (
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                                overflow: 'hidden',
                                flex: fullscreen ? 1 : 'none',
                                minHeight: 0,
                            }}>
                                <UnlayerEditor
                                    ref={editorRef}
                                    minHeight={fullscreen ? window.innerHeight - 130 : 600}
                                    onReady={() => {
                                        const ed = editorRef.current?.editor;
                                        if (!ed) return;
                                        const design = config.designs?.[activeLang];
                                        if (design) {
                                            try { ed.loadDesign(design); } catch { /* ignore */ }
                                        }
                                        // Auto-sync edits back to (parent) state so the
                                        // global Save captures them — without this the
                                        // only flush point is a language switch, so a
                                        // straight edit-then-save would be lost and the
                                        // dirty flag never flips. 400ms debounce keeps
                                        // us off every keystroke. activeLangRef avoids a
                                        // stale language in this once-bound listener.
                                        if (syncBoundRef.current) {
                                            if (syncBoundRef.current.ed === ed) return; // already bound to this editor
                                            try { syncBoundRef.current.ed.removeEventListener && syncBoundRef.current.ed.removeEventListener('design:updated', syncBoundRef.current.fn); } catch { /* ignore */ }
                                        }
                                        const sync = () => {
                                            if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
                                            syncTimerRef.current = setTimeout(() => {
                                                try {
                                                    ed.saveDesign((design2) => {
                                                        const lang = activeLangRef.current;
                                                        setConfig(p => ({ ...p, designs: { ...(p.designs || {}), [lang]: design2 } }));
                                                        try {
                                                            ed.exportHtml((data) => {
                                                                const html = data?.html || '';
                                                                setConfig(p => ({ ...p, html: { ...(p.html || {}), [lang]: html } }));
                                                            });
                                                        } catch { /* ignore */ }
                                                    });
                                                } catch { /* ignore */ }
                                            }, 400);
                                        };
                                        try { ed.addEventListener('design:updated', sync); syncBoundRef.current = { ed, fn: sync }; } catch { /* ignore */ }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Editor mode switch — full-width 2-column toggle below the editor.
                        Hidden in fullscreen so the user isn't tempted to switch modes
                        while the unlayer wrapper is detached. */}
                    {!fullscreen && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <EditorModeButton
                                active={config.editor === 'wysiwyg'}
                                onClick={() => switchEditor('wysiwyg')}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                label={tr('cfg_static_use_wysiwyg', 'Visual editor')}
                            />
                            <EditorModeButton
                                active={config.editor === 'unlayer'}
                                onClick={() => switchEditor('unlayer')}
                                icon={<CodeIcon fontSize="small" />}
                                label={tr('cfg_static_use_designer', 'HTML designer')}
                            />
                        </Box>
                    )}
                </Section>
            </Stack>
        </Box>
    );
};
StaticPageTab.propTypes = {
    theme:              PropTypes.string,
    t:                  PropTypes.func,
    pageKey:            PropTypes.oneOf(['about', 'disclaimer', 'privacy', 'intro', 'page404', 'detail']).isRequired,
    defaultTitle:       PropTypes.string,
    defaultDescription: PropTypes.string,
    value:              PropTypes.object,
    onChange:           PropTypes.func,
    hideHeader:         PropTypes.bool,
};

// ─────────────────────────────────────────────────────────────────────────
// Local helpers — same pattern as BannersTab / DynamicPageTab
// ─────────────────────────────────────────────────────────────────────────

const EditorModeButton = ({ active, onClick, icon, label }) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            py: 1.25, borderRadius: 1.5,
            border: '1.5px solid',
            borderColor: active ? 'primary.main' : 'divider',
            backgroundColor: active ? 'rgba(25,118,210,0.08)' : '#fff',
            color: active ? 'primary.main' : 'text.primary',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            fontWeight: 600,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main' },
        }}
    >
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    </ButtonBase>
);
EditorModeButton.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, icon: PropTypes.node, label: PropTypes.node };

// React.memo (F27): every keystroke elsewhere in the config screen used to
// re-render this whole editor tab; with the per-key setters memoised the
// props are stable, so memo can skip.
export default React.memo(StaticPageTab);
